import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { Popover, Box, TextField, Button, Typography, Link } from '@mui/material';
import { useNavigate } from 'react-router';
import { getLlmApiKey, setLlmApiKey } from '../../utils/llmApiKey';

/**
 * AR9b: lets the user enter their own personal-scope LLM API key. Stored in
 * localStorage and sent as X-LLM-API-Key, overriding the org/server key. This
 * is the SAME setting as Settings -> "API keys" (#1042 — the two must read as
 * one thing, not two competing places to set a key); this popover is the
 * quick-access path, the Settings tab the full one, and each links to the
 * other.
 *
 * #1038: the popover itself is unchanged, but this component no longer owns
 * its own trigger button — the caller (ChatInterface's burger-menu row, or the
 * "no key set" chat banner) opens it imperatively via the ref, anchored to
 * whichever control the user actually clicked. `onKeyChange` reports the
 * has-a-key boolean back up so the caller's menu row can show current status.
 */
// The JSDoc below has to sit directly on a function DECLARATION, not on the
// `const ApiKeySettings = forwardRef(...)` assignment that wraps it — a JSDoc
// above that `const` attaches to the variable statement, not to the render
// function nested inside its initializer, so the props/ref types never reach
// forwardRef's inference. Hoisting the render function out (as CommentComposer
// does, #968 Phase B) is what makes the JSDoc actually apply.
/**
 * @param {{ onKeyChange?: (hasKey: boolean) => void }} props
 * @param {React.ForwardedRef<{ open: (anchorElement: any) => void }>} ref
 */
function ApiKeySettingsImpl({ onKeyChange }, ref) {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [value, setValue] = useState(getLlmApiKey());
  const [hasKey, setHasKey] = useState(Boolean(getLlmApiKey()));

  useImperativeHandle(ref, () => ({
    open: (anchorElement) => { setValue(getLlmApiKey()); setAnchorEl(anchorElement); },
  }), []);

  const close = () => setAnchorEl(null);

  const save = () => {
    const trimmed = value.trim();
    setLlmApiKey(trimmed);
    setHasKey(Boolean(trimmed));
    onKeyChange?.(Boolean(trimmed));
    close();
  };
  const clear = () => {
    setLlmApiKey('');
    setValue('');
    setHasKey(false);
    onKeyChange?.(false);
  };
  const goToSettings = () => {
    close();
    navigate('/account/settings?tab=keys');
  };

  return (
    <Popover open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={close}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
      <Box sx={{ p: 2, width: 320 }}>
        <Typography variant="subtitle2" gutterBottom>Your personal LLM API key</Typography>
        <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'text.secondary' }}>
          Stored only in this browser; sent with your chat requests to override your
          organization&apos;s or the platform&apos;s key. Leave empty to use that default.
          This is the same key as Settings → API keys.
        </Typography>
        <TextField
          type="password" size="small" fullWidth autoComplete="off"
          placeholder="sk-or-..." value={value}
          onChange={(e) => setValue(e.target.value)}
          slotProps={{ htmlInput: { 'aria-label': 'LLM API key' } }}
        />
        <Box sx={{ mt: 1 }}>
          <Link component="button" type="button" variant="caption" onClick={goToSettings}>
            Manage all your keys in Settings
          </Link>
        </Box>
        <Box sx={{ mt: 1.5, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button size="small" onClick={clear} disabled={!hasKey && !value}>Clear</Button>
          <Button size="small" variant="contained" onClick={save}>Save</Button>
        </Box>
      </Box>
    </Popover>
  );
}

const ApiKeySettings = forwardRef(ApiKeySettingsImpl);

export default ApiKeySettings;
